import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import Combobox from '@/components/combo-box';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { getInitials } from '@/lib';
import type { Course, CourseTeacher } from '@/types/api/course.types';
import type { User } from '@/types';
import { EllipsisVertical, Plus, X } from 'lucide-react';

interface InstructorsTabProps {
  course?: Course;
  courseTeachers?: CourseTeacher[];
  canEdit: boolean;
  onOpenAddInstructor: () => void;
  onToggleInstructorActive: (teacherMappingId: number, active: boolean) => void;
  isPatchingTeacher: boolean;
  onOpenDeleteInstructor: (teacherMappingId: number) => void;
  isDeletingTeacher: boolean;
}

export const InstructorsTabContent = ({
  course,
  courseTeachers,
  canEdit,
  onOpenAddInstructor,
  onToggleInstructorActive,
  isPatchingTeacher,
  onOpenDeleteInstructor,
  isDeletingTeacher,
}: InstructorsTabProps) => {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
      {canEdit && (
        <button
          type="button"
          className="min-h-[180px] rounded-xl border-2 border-dashed border-muted-foreground/30 hover:border-primary/40 bg-background/70 transition-colors flex flex-col items-center justify-center gap-2 text-muted-foreground"
          onClick={onOpenAddInstructor}
        >
          <Plus className="h-5 w-5" />
          <span className="font-medium">Add instructor</span>
        </button>
      )}

      <div className="rounded-xl border bg-background overflow-hidden shadow-sm">
        <div className="h-20 bg-gradient-to-r from-slate-600 to-slate-700 px-4 py-3 text-white/90 text-sm">
          Main instructor
        </div>
        <div className="px-4 pb-5 -mt-10 flex flex-col items-center text-center">
          <Avatar className="h-20 w-20 border-4 border-background">
            <AvatarImage
              src={course?.lecturer?.photo_url || undefined}
              alt={course?.lecturer_full_name || 'Instructor'}
            />
            <AvatarFallback>
              {getInitials(
                course?.lecturer?.first_name || '',
                course?.lecturer?.last_name || ''
              )}
            </AvatarFallback>
          </Avatar>
          <p className="mt-3 text-lg font-semibold">{course?.lecturer_full_name}</p>
          <p className="text-sm text-muted-foreground break-all">
            {course?.lecturer?.email}
          </p>
        </div>
      </div>

      {courseTeachers?.map((teacherMap) => {
        const fullName = `${teacherMap.teacher.first_name} ${teacherMap.teacher.last_name}`;
        return (
          <div
            key={teacherMap.id}
            className={`rounded-xl border bg-background overflow-hidden shadow-sm ${
              teacherMap.active ? '' : 'opacity-60 saturate-50'
            }`}
          >
            <div className="h-20 bg-gradient-to-r from-slate-700 to-slate-800 px-4 py-3 text-white/90 text-sm flex items-start justify-between">
              <span>Instructor</span>
              {canEdit && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      type="button"
                      className="h-7 w-7 inline-flex items-center justify-center rounded-md hover:bg-white/15"
                    >
                      <EllipsisVertical className="h-4 w-4" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem
                      onClick={() =>
                        onToggleInstructorActive(teacherMap.id, teacherMap.active)
                      }
                      disabled={isPatchingTeacher}
                    >
                      {teacherMap.active ? 'Mark as Inactive' : 'Mark as Active'}
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      variant="destructive"
                      onClick={() => onOpenDeleteInstructor(teacherMap.id)}
                      disabled={isDeletingTeacher}
                    >
                      Remove
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>
            <div className="px-4 pb-5 -mt-10 flex flex-col items-center text-center">
              <Avatar className="h-20 w-20 border-4 border-background">
                <AvatarImage
                  src={teacherMap.teacher.photo_url || undefined}
                  alt={fullName}
                />
                <AvatarFallback>
                  {getInitials(
                    teacherMap.teacher.first_name || '',
                    teacherMap.teacher.last_name || ''
                  )}
                </AvatarFallback>
              </Avatar>
              <p className="mt-3 text-lg font-semibold">{fullName}</p>
              <p className="text-sm text-muted-foreground break-all">
                {teacherMap.teacher.email}
              </p>
            </div>
          </div>
        );
      })}
    </div>
  );
};

interface AddInstructorsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedInstructorIds: string[];
  selectedInstructorOption: string;
  setSelectedInstructorOption: (value: string) => void;
  availableInstructorOptions: { label: string; value: string }[];
  onAddInstructorSelection: (userId: string) => void;
  selectedInstructorUsers: User[];
  onRemoveSelectedInstructor: (userId: string) => void;
  onSubmit: () => void;
  isSubmitting: boolean;
  onResetSelection: () => void;
}

export const AddInstructorsDialog = ({
  open,
  onOpenChange,
  selectedInstructorIds,
  selectedInstructorOption,
  setSelectedInstructorOption,
  availableInstructorOptions,
  onAddInstructorSelection,
  selectedInstructorUsers,
  onRemoveSelectedInstructor,
  onSubmit,
  isSubmitting,
  onResetSelection,
}: AddInstructorsDialogProps) => {
  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        onOpenChange(next);
        if (!next) onResetSelection();
      }}
    >
      <DialogContent className="sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle>Add instructors</DialogTitle>
          <DialogDescription>
            Select users to add as instructors in this course.
          </DialogDescription>
        </DialogHeader>

        <div className="rounded-xl border overflow-hidden">
          <div className="grid grid-cols-1 md:grid-cols-2">
            <div className="p-4 border-b md:border-b-0 md:border-r">
              <p className="mb-2 text-sm font-medium">Add instructor</p>
              <Combobox
                options={availableInstructorOptions}
                selectedOption={selectedInstructorOption}
                onSelect={(value) => {
                  setSelectedInstructorOption(value);
                  onAddInstructorSelection(value);
                }}
                placeholder="Search user"
                searchable
              />
            </div>

            <div className="p-4">
              <p className="mb-2 text-sm font-medium">Selected users</p>
              <div className="flex flex-wrap gap-2 min-h-10">
                {selectedInstructorUsers.map((user) => (
                  <div
                    key={user.id}
                    className="inline-flex items-center gap-2 rounded-full border bg-muted px-3 py-1 text-sm"
                  >
                    <span>{`${user.profile.first_name} ${user.profile.last_name} (${user.email})`}</span>
                    <button
                      type="button"
                      onClick={() => onRemoveSelectedInstructor(user.id)}
                      className="inline-flex items-center justify-center rounded-full hover:bg-muted-foreground/20"
                      aria-label="Remove selected instructor"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ))}
                {!selectedInstructorUsers.length && (
                  <p className="text-sm text-muted-foreground">No instructor selected.</p>
                )}
              </div>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button onClick={onSubmit} disabled={!selectedInstructorIds.length || isSubmitting}>
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

interface RemoveInstructorDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  teacherMappingToDeleteData: CourseTeacher | null;
  onConfirm: () => void;
  isDeleting: boolean;
}

export const RemoveInstructorDialog = ({
  open,
  onOpenChange,
  teacherMappingToDeleteData,
  onConfirm,
  isDeleting,
}: RemoveInstructorDialogProps) => {
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Remove instructor?</AlertDialogTitle>
          <AlertDialogDescription>
            {teacherMappingToDeleteData
              ? `This will remove ${teacherMappingToDeleteData.teacher.first_name} ${teacherMappingToDeleteData.teacher.last_name} from this course.`
              : 'This will remove the instructor from this course.'}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction
            className="bg-destructive hover:bg-destructive/90"
            onClick={onConfirm}
            disabled={isDeleting}
          >
            Remove
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
